// Particle box. One honest rule: particle speed grows with temperature. Below the melting
// point particles sit in a lattice and jiggle; between melt and boil they slide past each
// other near the bottom; above boil they fly freely and fill the box. Headless-testable.

import type { Rng } from '@kit/rng';

export interface Particle { x: number; y: number; vx: number; vy: number; hx: number; hy: number }
export interface Sim {
  w: number; h: number; n: number; r: number;
  p: Particle[];
  squeeze: number;      // 1 = full width, 0.5 = half width (wall on the right moves in)
  wallHits: number;     // counted per step for the squeeze lesson
}

export const MELT_C = 0;
export const BOIL_C = 100;
export const MIN_C = -20;
export const MAX_C = 120;

export type Phase = 'solid' | 'liquid' | 'gas';
export function phaseOf(tC: number): Phase { return tC < MELT_C ? 'solid' : tC < BOIL_C ? 'liquid' : 'gas'; }

export function createSim(n: number, w: number, h: number, rng: Rng): Sim {
  const r = Math.max(4, Math.min(w, h) / 34);
  const cols = Math.ceil(Math.sqrt(n * (w / h)));
  const gap = r * 2.3;
  const p: Particle[] = [];
  const x0 = w / 2 - ((cols - 1) * gap) / 2;
  for (let i = 0; i < n; i++) {
    const c = i % cols, row = Math.floor(i / cols);
    const hx = x0 + c * gap, hy = h - r * 1.5 - row * gap;
    p.push({ x: hx, y: hy, vx: (rng() - 0.5) * 10, vy: (rng() - 0.5) * 10, hx, hy });
  }
  return { w, h, n, r, p, squeeze: 1, wallHits: 0 };
}

/** temperature in C -> a speed scale in px/s; roughly proportional to sqrt(T in kelvin) */
export function speedFor(tC: number, sim: Sim): number {
  const k = Math.max(1, tC + 273);
  const base = Math.min(sim.w, sim.h);
  return base * 0.0028 * Math.sqrt(k) * (phaseOf(tC) === 'gas' ? 1.6 : phaseOf(tC) === 'liquid' ? 1.0 : 0.55);
}

export function step(sim: Sim, tC: number, dt: number, rng: Rng) {
  const ph = phaseOf(tC);
  const target = speedFor(tC, sim);
  const right = sim.w * sim.squeeze;
  const r = sim.r;
  sim.wallHits = 0;
  for (const q of sim.p) {
    if (ph === 'solid') {
      // spring to lattice home + thermal jiggle
      const k = 60;
      q.vx += (q.hx - q.x) * k * dt + (rng() - 0.5) * target * 6 * dt * 60 * 0.08;
      q.vy += (q.hy - q.y) * k * dt + (rng() - 0.5) * target * 6 * dt * 60 * 0.08;
      q.vx *= 0.9; q.vy *= 0.9;
    } else if (ph === 'liquid') {
      // gravity toward the bottom, random nudges, damping; collisions keep them apart
      q.vy += 420 * dt;
      q.vx += (rng() - 0.5) * target * 2.5;
      q.vy += (rng() - 0.5) * target * 2.5;
      q.vx *= 0.96; q.vy *= 0.96;
    } else {
      // free flight; nudge speed toward the thermal target
      const sp = Math.hypot(q.vx, q.vy) || 1;
      const adj = 1 + (target * 1.1 / sp - 1) * 0.08;
      q.vx *= adj; q.vy *= adj;
      q.vx += (rng() - 0.5) * target * 0.3;
      q.vy += (rng() - 0.5) * target * 0.3;
    }
    q.x += q.vx * dt; q.y += q.vy * dt;
    // walls
    if (q.x < r) { q.x = r; q.vx = Math.abs(q.vx); sim.wallHits++; }
    if (q.x > right - r) { q.x = right - r; q.vx = -Math.abs(q.vx); sim.wallHits++; }
    if (q.y < r) { q.y = r; q.vy = Math.abs(q.vy); sim.wallHits++; }
    if (q.y > sim.h - r) { q.y = sim.h - r; q.vy = -Math.abs(q.vy); sim.wallHits++; }
  }
  // soft collisions (liquid and gas; solid relies on the lattice)
  if (ph !== 'solid') {
    const min = r * 2;
    for (let i = 0; i < sim.p.length; i++) for (let j = i + 1; j < sim.p.length; j++) {
      const a = sim.p[i], b = sim.p[j];
      const dx = b.x - a.x, dy = b.y - a.y;
      const d = Math.hypot(dx, dy);
      if (d > 0 && d < min) {
        const push = (min - d) / 2;
        const nx = dx / d, ny = dy / d;
        a.x -= nx * push; a.y -= ny * push; b.x += nx * push; b.y += ny * push;
        if (ph === 'gas') { // elastic-ish swap along the normal
          const va = a.vx * nx + a.vy * ny, vb = b.vx * nx + b.vy * ny;
          a.vx += (vb - va) * nx; a.vy += (vb - va) * ny; b.vx += (va - vb) * nx; b.vy += (va - vb) * ny;
        } else {
          a.vx -= nx * push * 4; a.vy -= ny * push * 4; b.vx += nx * push * 4; b.vy += ny * push * 4;
        }
      }
    }
  }
}

export interface Stats { meanSpeed: number; meanHomeDist: number; spread: number }
/** spread = fraction of the box height the particles occupy (gas fills it, solid and liquid sit low) */
export function stats(sim: Sim): Stats {
  let sp = 0, hd = 0, minY = sim.h, maxY = 0;
  for (const q of sim.p) {
    sp += Math.hypot(q.vx, q.vy);
    hd += Math.hypot(q.x - q.hx, q.y - q.hy);
    minY = Math.min(minY, q.y); maxY = Math.max(maxY, q.y);
  }
  return { meanSpeed: sp / sim.n, meanHomeDist: hd / sim.n, spread: (maxY - minY) / sim.h };
}
